import { useNectarProject } from '@/features/nectar/api'
import type { AudioAnnotationRatingField } from '@/features/nectar/api/models'
import { Button } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { clsx } from 'clsx'
import { ArrowLeft, Check, Loader2, Star } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { submitLabels } from '../../../api/conversations-api'

const formatDuration = (totalSec: number) => {
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  if (min === 0) return `${sec}s`
  return `${min}m ${sec}s`
}

interface RatingControlProps {
  min: number
  max: number
  value: number | null
  onChange: (v: number) => void
}

function RatingControl({ min, max, value, onChange }: RatingControlProps) {
  const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i)
  return (
    <div className="flex items-center gap-1">
      {steps.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={clsx(
            'flex size-9 items-center justify-center rounded-full transition-colors',
            value !== null && n <= value
              ? 'bg-orange text-white'
              : 'text-gray-80 hover:bg-orange/20 hover:text-orange bg-gray-100'
          )}
        >
          <Star className={clsx('size-4', value !== null && n <= value ? 'fill-white' : '')} />
        </button>
      ))}
    </div>
  )
}

export const ConversationCompleteStep = ({
  phase,
  projectId,
  sessionId,
  onBack
}: {
  phase: { durationSec: number }
  projectId: string
  sessionId: string
  onBack: () => void
}) => {
  const duration = formatDuration(phase.durationSec)

  const { data: project, isLoading: isLoadingProject } = useNectarProject(projectId)

  const ratingFields = (project?.labelFormSchema?.fields ?? []).filter(
    (f): f is AudioAnnotationRatingField => f.type === 'rating'
  )

  const [ratings, setRatings] = useState<Record<string, number | null>>({})
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const allFilled = ratingFields.length > 0 && ratingFields.every((f) => ratings[f.key] != null)

  const handleSubmitAll = async () => {
    if (!allFilled || submitting) return
    setSubmitting(true)
    try {
      const answers = Object.fromEntries(ratingFields.map((f) => [f.key, ratings[f.key]!]))
      await submitLabels(sessionId, answers)
      setFormSubmitted(true)
    } catch {
      toast.error('Failed to submit ratings')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center rounded-2xl border border-solid border-gray-100 px-4 py-8">
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="flex size-20 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
          <Check className="size-9" />
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">
            <Trans>Conversation Complete</Trans>
          </h2>
          {phase.durationSec > 0 && (
            <p className="text-muted-foreground text-sm">
              <Trans>Duration: {duration}</Trans>
            </p>
          )}
        </div>

        {isLoadingProject && <Loader2 className="text-gray-80 size-5 animate-spin" />}

        {ratingFields.length > 0 ? (
          <div className="flex w-full flex-col gap-1 px-6 py-5">
            {formSubmitted ? (
              <div className="text-green flex items-center justify-center gap-1.5 py-2 text-sm font-medium">
                <Check className="size-4" />
                <Trans>Ratings submitted</Trans>
              </div>
            ) : (
              <>
                {ratingFields.map((field, i) => (
                  <div
                    key={field.key}
                    className={clsx(
                      'flex items-center justify-between gap-4 py-4 max-mobile:flex-col',
                      i < ratingFields.length - 1 && 'border-b border-solid border-gray-100'
                    )}
                  >
                    <p className="text-gray-70 text-sm font-semibold">{field.label}</p>
                    {field.description && <p className="text-gray-80 text-xs">{field.description}</p>}
                    <RatingControl
                      min={field.min}
                      max={field.max}
                      value={ratings[field.key] ?? null}
                      onChange={(v) => setRatings((r) => ({ ...r, [field.key]: v }))}
                    />
                  </div>
                ))}
                <Button
                  size="small"
                  variant="green"
                  onClick={handleSubmitAll}
                  isDisabled={!allFilled}
                  isLoading={submitting}
                  className="mt-2 w-full"
                >
                  <Trans>Submit</Trans>
                </Button>
              </>
            )}
          </div>
        ) : (
          <Button variant="bordered" onClick={onBack}>
            <ArrowLeft className="size-4" />
            <Trans>Pick Another Project</Trans>
          </Button>
        )}
      </div>
    </div>
  )
}
